/**
 * Split a url — or a path such as next's `router.asPath`, which carries both — into the part
 * before its query, its query and its fragment.
 *
 * On the first delimiter only: a fragment can contain "?" (hash routers) and a query can
 * contain a raw one, and either would otherwise take the rest of the url with it.
 *
 * @returns the part before the query, the query without its "?", and the fragment without its
 * "#" — `null` when there is no fragment at all, which a bare trailing "#" is not
 */
export function splitUrl(url: string) {
  const hashAt = url.indexOf("#");
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = beforeHash.indexOf("?");
  return {
    baseUrl: queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt),
    query: queryAt === -1 ? "" : beforeHash.slice(queryAt + 1),
    hash: hashAt === -1 ? null : url.slice(hashAt + 1),
  };
}

function parseUrl(url: string) {
  try {
    return new URL(url);
  } catch (error) {
    return null;
  }
}

/**
 * The url as something we can safely put in an href, or `null` if it isn't one.
 *
 * Values here come from whatever an organizer typed into a call's website field, so both
 * halves matter:
 *
 * - only http(s) passes. `new URL()` alone is no guard at all — `javascript:`, `data:` and
 *   `mailto:` all parse, and react still renders a `javascript:` href (it only warns), which
 *   would run whatever the field contains on our own origin;
 * - a value with no scheme (`www.example.com`) is not one: as an href it resolves against our
 *   origin and takes the visitor to a page of ours that doesn't exist. Callers render nothing
 *   rather than guess at what was meant.
 *
 * A protocol-relative url is assumed https, since the browser resolved those correctly as
 * hrefs before we started checking and dropping them would lose a working destination.
 */
export function toWebUrl(url: string): string | null {
  const candidate = url.startsWith("//") ? `https:${url}` : url;
  const protocol = parseUrl(candidate)?.protocol;
  return protocol === "http:" || protocol === "https:" ? candidate : null;
}

/**
 * Whether a url points somewhere that isn't ours.
 *
 * Only external hosts are denied the visit's campaign params, and only they are marked up as
 * leaving the site, so this is the single definition both decisions rely on. Compares the
 * hostname rather than the host, so one of our hosts on an explicit port still counts as ours.
 *
 * A url we can't parse counts as external: that withholds the campaign params, which is the
 * safe side of the decision.
 */
export function isExternalUrl(url: string) {
  const parsed = parseUrl(url);
  return parsed ? !/(^|\.)picter\.com$/i.test(parsed.hostname) : true;
}
